import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { FlaskConical, Wrench, Clock, PackageCheck, Stethoscope, Plus, Wallet } from 'lucide-react'
import { useMe } from '../../lib/me.js'
import { usePatientLive } from '../../lib/patientLive.js'
import {
  Badge, Banner, Button, DataTable, EmptyState, ErrorState, Loading, PageHeader,
  fmtDate, fmtDateText, fmtMoney, useToast,
} from '../../kit'
import { nameInitial } from '../../lib/text.js'
import { REL_TONES } from '../../lib/ticket.js'
import StagePicker from '../../components/lab/StagePicker.jsx'
import LabDetailDrawer from '../../components/lab/LabDetailDrawer.jsx'
import LabCreateDrawer from '../../components/lab/LabCreateDrawer.jsx'
import useLabRefs from '../../lib/useLabRefs.js'
import {
  postLabEdit, postLabCreate, deleteLab,
  applyStagePatch, applyEditPatch,
} from '../../lib/labWrites.js'
import {
  fetchLabForSummary,
  labVendorName, labDoctorCode, labDoctorImg, labPrice,
  hasRetainer, isPriceLockedRow,
  labCardState, labDue, labDueRel, labTitle, labYear, isLabStale, labAgeDays,
} from '../../lib/lab.js'

// ── /patient/:id/lab — this patient's lab case history ───────────────────────
//
// /lab is the clinic's production board: every bench, bounded by ปี/เดือน, money
// first, bulk stage moves, payroll. This page is ONE MOUTH — unbounded in time,
// no filters, no bulk, no payroll (a month-wide payroll run has no per-patient
// meaning). It answers two questions in this order: is anything waiting on us,
// and what was ever made for them.
//
// The page is TWO MUTUALLY EXCLUSIVE LISTS, not a list plus a filtered copy of
// itself. `labCardState()` decides membership; every row lands in exactly one.
//
// ⚠️ Role gating is deliberately absent. Lab-only sessions are confined to
// /lab + /me by _middleware.js, and doctor-only sessions are <Navigate>d to the
// chart by PatientLayout — so `hideMoney`/`labOnly` branches would be dead code
// here. Don't add them back.
//
// ── WRITES (added 2026-08-17) ────────────────────────────────────────────────
// This page is where a lab record is CREATED and DESTROYED; /lab only edits the
// ones that exist. Both are open to every employee, and both stop dead at
// labstage 8 หักยอดทันตแพทย์แล้ว — the cost is already out of a doctor's salary by
// then, so the record is frozen whole.
//   • เพิ่ม  → LabCreateDrawer → POST /api/lab/record. การรักษา is REQUIRED: GET /lab
//     RIGHT-joins treatment, so an unlinked lab is written and then never readable.
//   • แก้ไข  → the same LabDetailDrawer /lab uses, now unlocked at every stage but 8.
//   • ลบ    → DELETE /api/lab/record. ⚠️ HARD delete upstream (db.del); the proxy
//     re-reads the row's stage and FAILS CLOSED before forwarding.
// A create or a delete triggers a full `load()` rather than a splice: the GET is what
// decides whether a new row is even readable (treatment RIGHT join), and a refetch is the
// only way to be sure the list matches what the server would return.

export default function PatientLab() {
  const { id } = useParams()
  // Plain useMe(): <AuthGate> renders the router only once status === 'ok', so `me`
  // is already resolved by the time any page mounts. An extra readiness gate here
  // would be dead code.
  const me = useMe()
  // The patient PatientLayout already fetched — the create drawer's header reads the
  // name and HN off it, so nothing extra is requested for it.
  const { patient, live } = useOutletContext() || {}
  const { stages, doctors, branchName, stageById, vendorMap } = useLabRefs()

  const [rows, setRows] = useState(null)      // null = loading
  const [error, setError] = useState(null)
  const [truncated, setTruncated] = useState(false)
  const [failed, setFailed] = useState(false)
  const [openRow, setOpenRow] = useState(null)
  const [adding, setAdding] = useState(false)
  const toast = useToast()

  // Card membership and tone are derived LIVE from `labstage_id` — no longer frozen at
  // fetch time (2026-08-18). The freeze existed because membership used to depend on stage
  // AND age, so advancing a card could filter the row out mid-write, unmounting the
  // <StagePicker> before its own setBusy(false), and could teleport a ledger row up into
  // ต้องติดตาม wearing a rose "เลยกำหนด 870 วัน" alarm.
  //
  // Neither can happen now. Membership turns on ONE thing — stage 8 — and the picker
  // cannot set stage 8: it is stripped from the options (`selectableStages`) and the proxy
  // 400s on it, because only the payroll run may settle a lab. So every stage a user can
  // choose keeps the row on the same side of the divide, and the list is keyed by id, so a
  // re-sort moves the component rather than unmounting it.
  //
  // ⚠️ If a future change lets the picker reach stage 8, or makes membership depend on
  // anything a write can alter, the freeze has to come back with it.
  // Every load carries a sequence number and only the newest may write state.
  // React Router keeps this component mounted across /patient/1/lab →
  // /patient/2/lab, and fetchLabForSummary can walk up to 3 pages, so patient 1's
  // answer routinely lands AFTER patient 2's — putting one patient's appliances
  // under another patient's name and HN, permanently, until the next navigation.
  const reqId = useRef(0)

  const load = useCallback(async () => {
    if (!id) return
    const seq = ++reqId.current
    setError(null)
    try {
      // fetchLabForSummary already does exactly this — `patient_id` is a real
      // server-side filter in the Xano where clause and is allowlisted in the
      // proxy. per_page 200 puts every realistic patient on ONE page, which
      // matters: GET /lab's live branch has override_sort but NO default `sort`,
      // so multi-page order is undefined and a walk could skip rows.
      const res = await fetchLabForSummary({ patient_id: id }, { perPage: 200, maxPages: 3 })
      if (seq !== reqId.current) return
      setRows(res.rows)
      setTruncated(res.truncated)
      // Returned, not just stored: createLab needs the freshly-loaded row to open its
      // drawer, and reading `rows` right after setRows() would still see the old array.
      return res.rows
    } catch (e) {
      if (seq !== reqId.current) return
      setError(e?.message || 'โหลดข้อมูล Lab ไม่สำเร็จ')
      // NOT setRows([]) alone: an errored list that renders as "ยังไม่มีงาน Lab"
      // tells a counter staffer this patient has no lab work. `failed` suppresses
      // the counts and the lists entirely, leaving only the banner.
      setRows([]); setFailed(true)
    }
  }, [id])

  // React Router keeps this component mounted across /patient/1/lab → /patient/2/lab,
  // so EVERY piece of row-derived state has to be cleared here — an open drawer left
  // behind would write patient 1's lab row from patient 2's page.
  useEffect(() => {
    setRows(null); setOpenRow(null)
    setTruncated(false); setFailed(false)
    setAdding(false)
    load()
  }, [load])

  // Live: any lab row for this patient — added, edited, deleted, or (by far the
  // most common) moved to another stage. The stage change is POST /lab/status,
  // which broadcast nothing at all before the `patient_update - lab` trigger.
  // `load()` alone: it does not clear `rows` first, so an open drawer survives.
  usePatientLive(live, ['lab'], () => { load() })

  /* ── the two lists ─────────────────────────────────────────────────────── */
  // Membership, tone and counts all come from `labCardState(row)` on the live row, so a
  // stage change repaints the card it was made on. See the note above for why that is
  // safe now and was not before.
  const { cards, ledger, stateById } = useMemo(() => {
    const cards = [], ledger = [], stateById = new Map()
    for (const r of rows || []) {
      const st = labCardState(r)
      if (st) { stateById.set(r.id, st); cards.push(r) } else ledger.push(r)
    }
    // 'ready' first — "their retainer is here, call them" is the page's first question,
    // and sorting purely by date buries it under work not due for weeks. 'settling' last:
    // the piece is made and delivered, it is payroll that has not caught up, so there is
    // nothing for the counter to do about it. Then by labDue (which prefers `deadline`).
    const rank = { ready: 0, inflight: 1, settling: 2 }
    cards.sort((a, b) =>
      (rank[stateById.get(a.id)] ?? 1) - (rank[stateById.get(b.id)] ?? 1)
      || labDue(b) - labDue(a))
    ledger.sort((a, b) => (Number(b.date) || 0) - (Number(a.date) || 0))
    return { cards, ledger, stateById }
  }, [rows])

  // Counted off the SAME map the cards render from, so the header can never say
  // "1 พร้อมรับ" while two ready cards are on screen.
  const counts = useMemo(() => {
    let inflight = 0, ready = 0, settling = 0
    for (const st of stateById.values()) {
      if (st === 'ready') ready++
      else if (st === 'settling') settling++
      else if (st === 'inflight') inflight++
    }
    return { total: (rows || []).length, inflight, ready, settling }
  }, [rows, stateById])

  /* ── writes (shared with /lab via lib/labWrites.js) ────────────────────── */
  const patchRowStage = useCallback((labId, newStageId) => {
    setRows((rs) => (rs || []).map((r) => (r.id === labId ? applyStagePatch(r, newStageId, stageById, me?.employee_id) : r)))
    setOpenRow((o) => (o && o.id === labId
      ? applyStagePatch(o, newStageId, stageById, me?.employee_id, { appendLog: true }) : o))
  }, [stageById, me])

  // Ledger columns. The list is grouped by Bangkok calendar year via DataTable's
  // `groupBy`: `ledger` is already date-desc, so years run newest first and the undated
  // (year 0) rows land last — the same order the old per-year header rows had.
  const ledgerColumns = useMemo(() => ledgerCols(stages, patchRowStage), [stages, patchRowStage])

  // ONE write for everything the drawer can change — detail, links, vendor, date, doctor,
  // price, production type and retainer — because upstream they were always one POST /lab
  // anyway. See functions/api/lab/edit.js.
  const saveLab = useCallback(async (row, payload) => {
    await postLabEdit(row, payload)
    const apply = (x) => (x && x.id === row.id ? applyEditPatch(x, payload, { vendorMap, doctors }) : x)
    setRows((rs) => (rs || []).map(apply))
    setOpenRow((o) => apply(o))
  }, [vendorMap, doctors])

  // CREATE. The new row is not spliced into `rows` — `load()` refetches, because the
  // GET is what decides whether the row is even readable (treatment RIGHT join) and
  // which of the two lists it belongs to. Then its drawer opens, so ประเภทงาน /
  // รีเทนเนอร์ / ค่า Lab — the three things the create form deliberately leaves out —
  // are one tap away instead of a second trip through the page.
  const createLab = useCallback(async (fields) => {
    const created = await postLabCreate({ ...fields, patient_id: Number(id) })
    setAdding(false)
    const res = await load()
    const fresh = (res || []).find((r) => Number(r.id) === Number(created?.id))
    if (fresh) { setOpenRow(fresh); toast.success('เพิ่มงาน Lab แล้ว') }
    else toast.info('เพิ่มงาน Lab แล้ว แต่ยังไม่แสดงในรายการ — ลองโหลดหน้าใหม่', { duration: 8000 })
  }, [id, load, toast])

  // DELETE — hard, upstream. The proxy re-checks the stage and refuses stage 8, so a
  // 409 here is the rule firing, not a bug; its Thai detail is what the drawer shows.
  const removeLab = useCallback(async (row) => {
    await deleteLab(row)
    setOpenRow(null)
    toast.success('ลบงาน Lab แล้ว')
    await load()
  }, [load, toast])

  const priceLocked = useCallback((row) => isPriceLockedRow(row, stageById), [stageById])

  /* ── render ────────────────────────────────────────────────────────────── */
  if (rows === null) {
    return (
      <div>
        <PageTitle counts={null} onAdd={null} />
        <Loading label="กำลังโหลดงาน Lab…" />
      </div>
    )
  }

  return (
    <div>
      <PageTitle counts={failed ? null : counts} onAdd={() => setAdding(true)} />

      {error && <ErrorState title="โหลดข้อมูล Lab ไม่สำเร็จ" message={error} className="mb-5" />}
      {truncated && <Banner tone="warn" className="mb-5">โหลดได้ไม่ครบ — แสดงเฉพาะบางส่วน</Banner>}

      {!failed && cards.length > 0 && (
        <section className="mb-7">
          <h3 className="text-[12.5px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-2.5">ต้องติดตาม</h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {cards.map((r) => (
              <LabCard
                key={r.id}
                row={r}
                state={stateById.get(r.id)}
                stages={stages}
                onOpen={() => setOpenRow(r)}
                onStageChanged={patchRowStage}
              />
            ))}
          </div>
        </section>
      )}

      {!failed && (
      <section>
        <h3 className="text-[12.5px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1">
          ประวัติงาน Lab
        </h3>
        {/* Says what the list IS. "History" alone would read as "everything old", which is
            exactly the reading this rule change removed. */}
        <p className="text-[12px] text-slate-400 mb-2.5">เฉพาะงานที่หักยอดทันตแพทย์แล้ว</p>

        {ledger.length === 0 ? (
          counts.total === 0
            ? <EmptyState icon={FlaskConical} title="ยังไม่มีงาน Lab ของคนไข้รายนี้"
                action={<Button variant="soft" size="sm" icon={Plus} onClick={() => setAdding(true)}>เพิ่มงาน Lab</Button>} />
            : <EmptyState variant="plain" title="ยังไม่มีงานที่หักยอดทันตแพทย์แล้ว" hint="ทั้งหมดอยู่ในรายการที่ต้องติดตามด้านบน" />
        ) : (
          <DataTable
            columns={ledgerColumns}
            rows={ledger}
            onRowClick={setOpenRow}
            groupBy={labYear}
            groupHeader={(year, list) => (
              <span className="tabular-nums">
                {year || 'ไม่ระบุปี'}
                <span className="text-slate-400 font-normal"> · {list.length}</span>
              </span>
            )}
          />
        )}

        {/* Permanent, not empty-state-only. GET /lab RIGHT-joins treatment, so a lab
            with treatment_id = 0 never arrives — 8,399 of 38,742 rows (21.7 %) as of
            2026-08-12 — and the where clause also drops lab.active == false. This page
            genuinely cannot claim to show everything, so it says so where it can be
            read against a populated list, not only when the list is empty. */}
        <p className="mt-2.5 text-[12px] text-slate-400">
          งาน Lab ที่ยังไม่ผูกกับการรักษา หรือถูกลบไปแล้ว จะไม่แสดงที่นี่
        </p>
      </section>
      )}

      {/* Conditional render is REQUIRED: LabDetailDrawer does `if (!row) return null`
          before its own useState/useEffect, so an unconditional <LabDetailDrawer
          row={openRow}/> throws "Rendered fewer hooks than expected" on close. */}
      {/* showPatientLink={false}: both routes out of the drawer open /patient/:id in a new
          tab, which from this page is a second tab of the page you are already on. */}
      {openRow && (
        <LabDetailDrawer
          row={openRow}
          stages={stages}
          branchName={branchName}
          hideMoney={false}
          priceLocked={priceLocked(openRow)}
          onSave={saveLab}
          onDelete={removeLab}
          showPatientLink={false}
          onClose={() => setOpenRow(null)}
          onChanged={patchRowStage}
        />
      )}

      {adding && (
        <LabCreateDrawer
          patientId={Number(id)}
          patientName={[patient?.name, patient?.surname].filter(Boolean).join(' ') || patient?.nickname || ''}
          patientHN={patient?.HN || ''}
          onCreate={createLab}
          onClose={() => setAdding(false)}
        />
      )}

    </div>
  )
}

/* -------------------------------------------------------------------------- */

// Sub-page header: PageHeader level 2 (PatientLayout supplies the page padding — no <Page>).
function PageTitle({ counts, onAdd }) {
  const meta = counts && (
    <div className="flex items-center gap-2.5 flex-wrap">
      <span>{counts.total} ชิ้น</span>
      {counts.inflight > 0 && (
        <><span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
            <Clock size={13} strokeWidth={2.25} />{counts.inflight} กำลังดำเนินการ
          </span></>
      )}
      {counts.ready > 0 && (
        <><span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
            <PackageCheck size={13} strokeWidth={2.25} />{counts.ready} พร้อมรับ
          </span></>
      )}
      {counts.settling > 0 && (
        <><span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 text-violet-600 font-medium">
            <Wallet size={13} strokeWidth={2.25} />{counts.settling} รอสรุปหักหมอ
          </span></>
      )}
    </div>
  )
  return (
    <PageHeader
      level={2}
      title="Lab"
      meta={meta}
      className="mb-5"
      actions={onAdd && <Button icon={Plus} onClick={onAdd}>เพิ่มงาน Lab</Button>}
    />
  )
}

// Every lab that is not หักยอดทันตแพทย์แล้ว gets a card — see lib/lab.js#labCardState.
// 'ready' (stage 6) is the piece sitting at our counter, the most actionable state on the
// page; 'settling' (stage 7) is made and delivered, waiting only on payroll.
// `state` is passed in, not recomputed: it is frozen at fetch time so a stage change
// cannot silently re-tone the card the user is looking at.
function LabCard({ row, state, stages, onOpen, onStageChanged }) {
  const ready = state === 'ready'
  const settling = state === 'settling'
  // Age drives TONE, never membership. A long-open piece reads as forgotten (slate), not
  // urgent (rose) — a rose "เลยกำหนด 870 วัน" on an ortho patient's page is pure noise.
  const stale = isLabStale(row)
  const rel = (ready || settling || stale) ? null : labDueRel(row)
  const img = labDoctorImg(row)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      className={`rounded-2xl border bg-white p-3.5 cursor-pointer transition hover:shadow-sm focus-visible:ring-2 focus-visible:ring-brand-200 focus-visible:outline-none ${
        ready ? 'border-emerald-200 bg-emerald-50/30'
          : settling ? 'border-violet-200 bg-violet-50/20'
            : 'border-slate-200/80 hover:border-slate-300'
      }`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        {ready
          ? <PackageCheck size={15} className="text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} />
          : settling
            ? <Wallet size={15} className="text-violet-500 mt-0.5 shrink-0" strokeWidth={2} />
            : <FlaskConical size={15} className="text-slate-400 mt-0.5 shrink-0" strokeWidth={1.75} />}
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium text-slate-900 leading-snug break-words">{labTitle(row)}</div>
          {ready
            ? <div className="text-[12.5px] font-medium text-emerald-700 mt-0.5">พร้อมรับ · โทรนัดรับได้</div>
            : settling
              ? <div className="text-[12.5px] font-medium text-violet-700 mt-0.5">รอสรุปหักหมอ</div>
              : stale
                ? <div className="text-[12.5px] text-slate-500 mt-0.5">{labAgeDays(row) == null ? 'ไม่ระบุวันที่' : `ค้างมาแล้ว ${labAgeDays(row)} วัน`}</div>
                : rel && <div className={`text-[12.5px] mt-0.5 ${REL_TONES[rel.tone].text}`}>{rel.label}</div>}
        </div>
        {row.repair && <Badge tone="amber" className="shrink-0"><Wrench size={10} /> แก้</Badge>}
      </div>

      <div className="text-[12.5px] text-slate-500 flex items-center gap-1.5 flex-wrap mb-2.5">
        <span className="truncate max-w-[140px]">{labVendorName(row)}</span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-1">
          {img
            ? <img src={img} alt="" className="w-4 h-4 rounded-full object-cover bg-slate-100" />
            : <Stethoscope size={11} className="text-slate-400" />}
          {labDoctorCode(row)}
        </span>
        <span className="text-slate-300">·</span>
        <span className="tabular-nums">ครบกำหนด {fmtDateText(labDue(row))}</span>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <StagePicker row={row} stages={stages} onChanged={onStageChanged} />
      </div>
    </div>
  )
}

// The settled-ledger columns (same columns and order as the hand-built table they replace).
// The สถานะ cell swallows clicks and keys so the StagePicker never also opens the row.
function ledgerCols(stages, onStageChanged) {
  const stop = (e) => e.stopPropagation()
  return [
    { key: 'date', label: 'วันที่', width: '104px', className: 'whitespace-nowrap text-slate-600 tabular-nums', render: (r) => fmtDate(r.date) },
    {
      key: 'title', label: 'งาน',
      render: (r) => (
        <>
          <div className="text-slate-800 break-words">{labTitle(r)}</div>
          {/* No "ค้างมาแล้ว" tag here: this table holds only settled labs, so isLabStale()
              is false for every row in it. Staleness shows on the cards above. */}
          {(r.repair || hasRetainer(r.retainer)) && (
            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
              {r.repair && <Badge tone="amber"><Wrench size={10} /> แก้</Badge>}
              {hasRetainer(r.retainer) && <Badge tone="violet">RTN</Badge>}
            </div>
          )}
        </>
      ),
    },
    { key: 'vendor', label: 'Lab', width: '150px', className: 'text-slate-500', render: (r) => <span className="line-clamp-1">{labVendorName(r)}</span> },
    {
      key: 'doctor', label: 'หมอ', width: '130px',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {labDoctorImg(r)
            ? <img src={labDoctorImg(r)} alt="" className="w-5 h-5 rounded-full object-cover shrink-0 bg-slate-100 border border-slate-200" />
            : <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{nameInitial(labDoctorCode(r))}</span>}
          <span className="text-slate-600">{labDoctorCode(r)}</span>
        </span>
      ),
    },
    { key: 'price', label: 'ต้นทุน Lab', width: '110px', align: 'right', render: (r) => fmtMoney(r?.price?.price == null ? null : labPrice(r)) },
    {
      key: 'stage', label: 'สถานะ', width: '168px',
      render: (r) => (
        <div onClick={stop} onKeyDown={stop}>
          <StagePicker row={r} stages={stages} onChanged={onStageChanged} />
        </div>
      ),
    },
  ]
}
